import { jsx as _jsx } from "react/jsx-runtime";
/* istanbul ignore file */
import { ImmutSet } from '@eturi/util';
import random from 'lodash/random';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { useSelector } from 'react-redux';
import { v4 } from 'uuid';
import { useChgState } from './useChgState';
import { useConstant } from './useConstant';
import { useFn } from './useFn';
import { useInteractionEffect } from './useInteractionEffect';
import { useInteractionFn } from './useInteractionFn';
import { useInteractionInterval } from './useInteractionInterval';
import { usePrevious } from './usePrevious';
import { useStateGetter } from './useStateGetter';
const PollingContext = createContext(null);
const DEFAULT_DURATIONS = {
    fast: 5_000, // 5s
    normal: 15_000, // 15s
    long: 30_000, // 30s
    extended: 360_000, // 6m
};
/**
 * Creates a polling hook and provider tuple given a `shouldPoll$` selector
 * and any configuration options.
 */
export const createPollingHook = (shouldPoll$, pollingOpts) => {
    const getArgs = pollingOpts.getArgs;
    const DEFAULT_IMMEDIATE = pollingOpts.immediate ?? false;
    const NORMALIZED_DURATIONS = {
        ...DEFAULT_DURATIONS,
        ...pollingOpts.durations,
    };
    const usePolling = (fns, interval = NORMALIZED_DURATIONS.normal, 
    // Immediate is set to false by default if interval is null
    immediate = interval != null && DEFAULT_IMMEDIATE) => {
        const ctx = useContext(PollingContext);
        const POLLING_ID = useConstant(v4);
        const fnsRef = useRef(fns);
        fnsRef.current = fns;
        const [didTriggerImmediate, setImmediateTriggered] = useState(false);
        const [hasIntervalPassed, setHasIntervalPassed] = useStateGetter(immediate);
        const [isPollingLocal, setPollingLocal] = useChgState(true);
        const [setTimeout, clearTimeouts] = useTimeouts();
        const isPolling = isPollingLocal && ctx.isPolling;
        const shouldCancelInterval = hasIntervalPassed() && !isPolling;
        const wasPolling = usePrevious(isPolling) ?? true;
        const triggerPolling = useInteractionFn((isImmediate = false) => {
            if (!isPolling || !hasIntervalPassed())
                return;
            setHasIntervalPassed(false);
            const arg = getArgs(isImmediate);
            fnsRef.current.forEach((fn) => {
                // Stagger each polling function a bit to avoid flooding the server / connection
                // Immediate polling staggers from 1-250ms. Regular polling staggers up to 5 seconds
                // based on interval.
                const upper = isImmediate ? 250 : Math.min(5000, 1000 + (interval || 0) * 0.15);
                const timeoutMs = Math.floor(random(1, upper));
                setTimeout(() => fn(arg), timeoutMs);
            });
        });
        useInteractionInterval(() => {
            setHasIntervalPassed(true);
            triggerPolling();
        }, shouldCancelInterval ? null : interval);
        useInteractionEffect(() => {
            if (!isPolling)
                return clearTimeouts();
            if (immediate && !didTriggerImmediate) {
                // Trigger if we're polling and still need to call immediate polling
                triggerPolling(true);
                setImmediateTriggered(true);
            }
            else if (!wasPolling) {
                // Trigger if we're polling, but we weren't polling previously
                setTimeout(triggerPolling, random(250, 1000));
            }
        }, [isPolling]);
        const pausePolling = useFn((type = 'local') => {
            type === 'local' ? setPollingLocal(false) : ctx.pause(POLLING_ID, type === 'global');
        });
        const resumePolling = useFn((type = 'local') => {
            type === 'local' ? setPollingLocal(true) : ctx.resume(POLLING_ID, type === 'global');
        });
        // Always resume 'global' and 'subtree' polling when the component
        // unmounts. Since a "paused" state is based on ids, this just removes the
        // ids for this component. This is only to prevent a state where a
        // component becomes unmounted and never turns polling back on.
        useEffect(() => () => {
            ctx.resume(POLLING_ID, true);
            ctx.resume(POLLING_ID, false);
        }, []);
        return [pausePolling, resumePolling, isPolling];
    };
    usePolling.FAST = NORMALIZED_DURATIONS.fast;
    usePolling.NORMAL = NORMALIZED_DURATIONS.normal;
    usePolling.LONG = NORMALIZED_DURATIONS.long;
    usePolling.EXTENDED = NORMALIZED_DURATIONS.extended;
    const PollingProvider = ({ children }) => {
        const shouldPoll = useSelector(shouldPoll$);
        const parentCtx = useContext(PollingContext);
        // We use stateGetter to avoid children overwriting state when updating simultaneously
        const [pollingPauseIds, setPollingPauseIds] = useStateGetter(() => new ImmutSet());
        const pause = useFn((id, isGlobal) => {
            if (isGlobal && parentCtx)
                return parentCtx.pause(id, isGlobal);
            setPollingPauseIds(pollingPauseIds().add(id));
        });
        const resume = useFn((id, isGlobal) => {
            if (isGlobal && parentCtx)
                return parentCtx.resume(id, isGlobal);
            setPollingPauseIds(pollingPauseIds().delete(id));
        });
        const isParentPolling = parentCtx?.isPolling ?? true;
        const isPolling = isParentPolling && shouldPoll && !pollingPauseIds().size;
        const ctx = useMemo(() => ({ isPolling, pause, resume }), [isPolling]);
        return _jsx(PollingContext.Provider, { value: ctx, children: children });
    };
    const usePausePolling = (isPaused, type = 'subtree') => {
        const { isPolling, pause, resume } = useContext(PollingContext);
        const POLLING_ID = useConstant(v4);
        useLayoutEffect(() => {
            if (isPolling !== isPaused)
                return;
            const isGlobal = type === 'global';
            isPaused ? pause(POLLING_ID, isGlobal) : resume(POLLING_ID, isGlobal);
        }, [isPaused]);
        // Resume polling on unmount, same as `usePolling` effect (see above)
        useEffect(() => () => {
            resume(POLLING_ID, true);
            resume(POLLING_ID, false);
        }, []);
        return isPolling;
    };
    return [PollingProvider, usePolling, usePausePolling];
};
// Just a helper to keep track of timeout lifecycles and clear everything when
// unmounting a component that has `usePolling`.
const useTimeouts = () => {
    const timeoutIds = useConstant(() => new Set());
    const setTimeout = useCallback((fn, ms = 0) => {
        const timeoutId = window.setTimeout(() => {
            timeoutIds.delete(timeoutId);
            fn();
        }, ms);
        timeoutIds.add(timeoutId);
    }, []);
    const clearTimeouts = useCallback(() => {
        timeoutIds.forEach(window.clearTimeout);
    }, []);
    useEffect(() => clearTimeouts, []);
    return [setTimeout, clearTimeouts];
};
//# sourceMappingURL=usePolling.js.map